import { DownloadOperation, PostDownloadOperation } from '@infuse/api/common';
import { ExportRolesAction } from '@infuse/iam/role/type';
import { UiNotificationType } from '@infuse/ui/domain';
import { useNotify } from '@infuse/ui/services';
import { getAxiosErrorMessage } from '@infuse/utils';
import { useMutation } from '@tanstack/vue-query';
import { AxiosResponse } from 'axios';
import { useI18n } from 'vue-i18n';
import { readDownloadFileName } from './exportFileName';

/**
 * Downloads one or more roles as a JSON file.
 *
 * The server decides the file name, so it is read back off the `Content-Disposition` header rather
 * than rebuilt here. That keeps every export, at every level, named the same way.
 */
export const useGenericExportRoles = ({
  exportOneFn,
  exportManyFn,
  forcedParentId
}: {
  exportOneFn: DownloadOperation<{ id?: string; roleId: string }>;
  exportManyFn: PostDownloadOperation<{ id?: string }, ExportRolesAction>;
  forcedParentId?: string;
}) => {
  const notify = useNotify();
  const { t } = useI18n({ useScope: 'global' });

  const mutation = useMutation<unknown, unknown, { parentId?: string; roleIds: string[] }>({
    mutationFn: async ({ parentId, roleIds }) => {
      if (forcedParentId) {
        parentId = forcedParentId;
      }

      if (roleIds.length === 0) {
        return;
      }

      try {
        // A single role has its own endpoint, so exporting one role never needs the additional
        // permission for handling several at once.
        const response =
          roleIds.length === 1
            ? await exportOneFn({ id: parentId, roleId: roleIds[0] })
            : await exportManyFn({ id: parentId }, { roleIds });

        saveAs(response, readDownloadFileName(response.headers as Record<string, unknown>));

        notify({
          title: t('iam.roles.header.export_roles'),
          message: t('iam.roles.notify.export_roles_success', { count: roleIds.length })
        });
      } catch (e) {
        notify({
          type: UiNotificationType.ERROR,
          title: t('iam.roles.header.export_roles'),
          message: getAxiosErrorMessage(e, t('iam.roles.notify.export_roles_failed'))
        });
        throw e;
      }
    }
  });

  return {
    ...mutation
  };
};

function saveAs(response: AxiosResponse<Blob>, fileName: string): void {
  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/json' }));
  const link = document.createElement('a');

  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
